/**
 * Billing Service
 * Handles billing, credits, and payment API calls
 * CD-291: Balance display
 * CD-292: Transaction history
 * CD-293: Buy credits page
 * CD-294: Payment methods (Stripe Link)
 * CD-295: Auto-charge configuration
 */
import api from './api'

export default {
  // Balance Operations

  /**
   * Get current credit balance
   * @returns {Promise<{balance: number, currency: string}>}
   */
  async getBalance() {
    return api.get('/billing/balance')
  },

  // Transaction History

  /**
   * Get transaction history
   * @param {object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.type - Filter by type (credit, debit, refund, adjustment)
   * @returns {Promise<{transactions: Array, pagination: object}>}
   */
  async getTransactions(params = {}) {
    return api.get('/billing/transactions', { params })
  },

  // Credit Packages

  /**
   * Get available credit packages
   * @returns {Promise<{packages: Array, user_currency: string, exchange_rate: number}>}
   */
  async getPackages() {
    return api.get('/billing/packages')
  },

  /**
   * Create checkout session for purchasing credits
   * @param {number} packageId - Credit package ID
   * @returns {Promise<{checkout_url: string, session_id: string}>}
   */
  async createCheckoutSession(packageId) {
    return api.post('/billing/checkout', { package_id: packageId })
  },

  /**
   * Verify checkout session status
   * @param {string} sessionId - Stripe session ID
   * @returns {Promise<{status: string, credits: number, new_balance: number}>}
   */
  async verifyCheckoutSession(sessionId) {
    return api.get(`/billing/checkout/${sessionId}/verify`)
  },

  // Payment Methods (Stripe Link)

  /**
   * Get saved payment methods
   * @returns {Promise<{payment_methods: Array, has_default: boolean}>}
   */
  async getPaymentMethods() {
    return api.get('/billing/payment-methods')
  },

  /**
   * Create setup intent for saving a new payment method
   * @returns {Promise<{client_secret: string}>}
   */
  async createSetupIntent() {
    return api.post('/billing/payment-methods/setup')
  },

  /**
   * Set default payment method
   * @param {string} paymentMethodId - Stripe payment method ID
   * @returns {Promise<{message: string}>}
   */
  async setDefaultPaymentMethod(paymentMethodId) {
    return api.post(`/billing/payment-methods/${paymentMethodId}/set-default`)
  },

  /**
   * Delete a payment method
   * @param {string} paymentMethodId - Stripe payment method ID
   * @returns {Promise<{message: string}>}
   */
  async deletePaymentMethod(paymentMethodId) {
    return api.delete(`/billing/payment-methods/${paymentMethodId}`)
  },

  // Auto-Charge Configuration

  /**
   * Get auto-charge configuration
   * @returns {Promise<{enabled: boolean, package_id: number, threshold: number}>}
   */
  async getAutoChargeConfig() {
    return api.get('/billing/auto-charge')
  },

  /**
   * Update auto-charge configuration
   * @param {object} config - Auto-charge settings
   * @param {boolean} config.enabled - Enable/disable auto-charge
   * @param {number} config.package_id - Package to auto-purchase
   * @param {number} config.threshold - Credit threshold to trigger
   * @returns {Promise<{message: string}>}
   */
  async updateAutoCharge(config) {
    return api.put('/billing/auto-charge', config)
  },

  // Invoices

  /**
   * Get invoice list
   * @param {object} params - Query parameters
   * @returns {Promise<{invoices: Array, pagination: object}>}
   */
  async getInvoices(params = {}) {
    return api.get('/billing/invoices', { params })
  },

  /**
   * Download invoice PDF
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise<Blob>}
   */
  async downloadInvoice(invoiceId) {
    return api.get(`/billing/invoices/${invoiceId}/download`, {
      responseType: 'blob'
    })
  }
}
